import React, { useContext, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import AddIcon from "@mui/icons-material/Add";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import { orderService, planningUserTaskService, userService } from "../../../../Core/Api/services";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { DbOrderManualTaskTemplateInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-infos";
import { DbOrderProductInfos } from "../../../../Core/Api/Types/db-order-product-infos";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import { DbOrderManualTaskTemplateFormFieldInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-form-field-infos";
import { DbOrderManualTaskJobInfos } from "../../../../Core/Api/Types/db-order-manual-task-job-infos";
import MetadataBoxAccordion from "../../../../Components/Misc/Metadata/MetadataBoxAccordion";
import TableCellDescription from "../../../../Components/Table/TableCellDescription";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { routePaths } from "../../../../Router/router";
import { produce } from "immer";
import { ManualTasktemplateAccessibility } from "../../../../Core/Api/Types/manual-tasktemplate-accessibility";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { OrderERPCreationMode } from "../../../../Core/Api/Types/order-erp-creation-mode";
import { DbOrderManualTaskServiceInfos } from "../../../../Core/Api/Types/db-order-manual-task-service-infos";
import { OrderManualTaskServiceTaskAssociation } from "../../../../Core/Api/Types/order-manual-task-service-task-association";
import { MetadataFieldBaseTypeEnum } from "../../../../Core/Api/Types/metadata-field-base-type-enum";
import { MetadataFieldValueCardinality } from "../../../../Core/Api/Types/metadata-field-value-cardinality";
import { DbOrderManualTaskTasksInfos } from "../../../../Core/Api/Types/db-order-manual-task-tasks-infos";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { GetSectionIsValid, GetFormIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../../Redux/redux-store";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { OrderManualTaskTemplateTypes } from "../../../../Core/Api/Types/order-manual-task-template-types";
import { ERPWorkOrderHeaderInfos } from "../../../../Core/Api/Types/erp-work-order-header-infos";
import { ERPWorkOrderTriggerInfos } from "../../../../Core/Api/Types/erp-work-order-trigger-infos";
import { ModalContext } from "../../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { useTranslation } from "react-i18next";

function AdminSettingsManualTasksTemplateConfig() {
  const { t } = useTranslation();
  const $dashboardRef = React.useRef<HTMLDivElement>(null);
  const params = useParams();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [template, setTemplate] = useState<DbOrderManualTaskTemplateInfos>();
  const [products, setProducts] = useState<DbOrderProductInfos[]>([]);
  const [groups, setGroups] = useState<GroupAdvancedInfos[]>([]);
  const [context, setContext] = useState<"Create" | "Update">("Create");
  const [newJob, setNewJob] = useState<DbOrderManualTaskJobInfos>(
    new DbOrderManualTaskJobInfos({
      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbOrderManualTaskJobInfos, VDM.AirLab.Sdk.Core",
    })
  );
  const [services, setServices] = useState<DbOrderManualTaskServiceInfos[]>([]);
  const [tasks, setTasks] = useState<OrderManualTaskServiceTaskAssociation[]>([]);
  const [newMeta, setNewMeta] = useState<DbOrderManualTaskTemplateFormFieldInfos>(
    new DbOrderManualTaskTemplateFormFieldInfos({
      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbOrderManualTaskTemplateFormFieldInfos, VDM.AirLab.Sdk.Core",
    })
  );
  const [newTasksJob, setNewTasksJob] = useState<
    { name: string; newName: string; taskType: DbOrderManualTaskTasksInfos | null }[]
  >([]);

  const formId = useMemo(() => `form-create-template`, []);
  const formValid = useAppSelector(GetFormIsValid(formId));
  const CreateTemplateGlobalSectionId = useMemo(() => `${formId}-configure-global-section`, []);
  const isGlobalConfigureSectionValid = useAppSelector(GetSectionIsValid(formId, CreateTemplateGlobalSectionId));
  const CreateTemplateERPSectionId = useMemo(() => `${formId}-configure-erp-section`, []);
  const isERPConfigureSectionValid = useAppSelector(GetSectionIsValid(formId, CreateTemplateERPSectionId));

  const handleCreate = () => {
    planningUserTaskService.CreateManualTasksTemplate(template!).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.templateCreated"), "success");
        navigate(routePaths.settingsManualTasks.path);
      } else enqueueCustomSnackbar(response.message!, "error");
    });
  };
  const handleUpdate = () => {
    planningUserTaskService.UpdateManualTasksTemplate(template!).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.templateUpdated"), "success");
        navigate(routePaths.settingsManualTasks.path);
      } else enqueueCustomSnackbar(response.message!, "error");
    });
  };

  const handleDelete = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.confirmDeleteTemplate"),
      title: t("modal:title.deleteTemplate"),
      onConfirm: () => {
        planningUserTaskService.DeleteManualTasksTemplate(template!.id).then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.templateDeleted"), "success");
            navigate(routePaths.settingsManualTasks.path);
          } else {
            enqueueCustomSnackbar(response.message!, "error");
          }
        });
      },
    });
  };

  const GetTotalPrice = (currency) => {
    let price = 0;

    if (template && template.jobs) {
      template.jobs.forEach((job) => {
        let priceAdd = job.tasks?.reduce((accumulator, task) => {
          const priceObject = task.prices?.find((x) => x.currency === (currency === "EUR" ? "EUR" : "USD"));
          return accumulator + (priceObject?.price || 0);
        }, 0);

        if (priceAdd) price += priceAdd;
      });
    }

    return price;
  };

  useEffect(() => {
    let taskObj: { name: string; newName: string; taskType: DbOrderManualTaskTasksInfos | null }[] = [];

    if (params.id === "new") {
      setTemplate(
        new DbOrderManualTaskTemplateInfos({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbOrderManualTaskTemplateInfos, VDM.AirLab.Sdk.Core",
          isInternal: false,
          createUserTasks: false,
          isTreatedExternally: false,
          isSystem: false,
          createdDate: new Date(),
          type: OrderManualTaskTemplateTypes.Generic,
          erpHeaderInfos: new ERPWorkOrderHeaderInfos(),
          erpTriggerInfos: new ERPWorkOrderTriggerInfos({
            erpWorkOrderCompleteTaskOnCompletedWorkflow: false,
            erpWorkOrderCreationMode: OrderERPCreationMode.OnOrderStart,
          }),
        })
      );
    } else {
      setContext("Update");
      //Get template
      planningUserTaskService.GetManualTasksTemplateById(parseInt(params.id!)).then((response) => {
        if (response.template) {
          setTemplate(response.template);

          response.template.jobs?.forEach((job) => {
            taskObj.push({ name: job.name || "", newName: "", taskType: null });
          });
          setNewTasksJob(taskObj);
        }
      });
    }

    // Get Product Types
    orderService.GetDeliveryProductsList().then((response) => {
      if (response.products) {
        setProducts(response.products);
      }
    });

    // Get Groups
    userService.GetGroupsList().then((response) => {
      if (response.groups) {
        setGroups(response.groups);
      }
    });

    // Get services
    planningUserTaskService.GetManualTasksTasksGroupedByServices().then((response) => {
      let services: DbOrderManualTaskServiceInfos[] = [];

      if (response.tasks) {
        setTasks(response.tasks);
        response.tasks.forEach((task) => {
          if (task.service) {
            services.push(task.service);
          }
        });
      }
      setServices(services);
    });
  }, []);

  return (
    <DashboardLayout ref={$dashboardRef} showTopbarSearchOnScroll>
      <Stack>
        <Link
          color="white"
          underline="hover"
          component={RouterLink}
          to={routePaths.settingsManualTasks.path}
          sx={{ mt: 1, ml: 2 }}
        >
          <ArrowBackRoundedIcon sx={{ mr: 0.5 }} />
          {t("button:backToTemplates")}
        </Link>

        {template && (
          <Box p={4}>
            <Paper elevation={0} sx={{ backgroundImage: "none", p: 3, mb: 2 }}>
              <Stack mb={3}>
                <Typography mb={1} variant="h3">
                  {t("page:body.templateManager")}
                </Typography>
                {context === "Create" ? (
                  <Typography variant="body2">{t("page:body.createNewTemplate")}</Typography>
                ) : (
                  <Typography variant="body2">
                    {t("page:body.updateExistingTemplate", { id: template!.id, description: template!.description })}
                  </Typography>
                )}
              </Stack>

              {/* Global Infos */}
              <FormSection
                status={isGlobalConfigureSectionValid ? "complete" : "warning"}
                statusText={isGlobalConfigureSectionValid ? t("common:configured") : t("common:configurationRequired")}
                title={t("common:global")}
                sx={{ mb: 2 }}
              >
                <Grid container spacing={2} mb={1}>
                  <Grid item md={6}>
                    <FormInputText
                      label={t("common:name")}
                      placeholder={t("common:name")}
                      value={template?.description}
                      onChange={(v) => {
                        setTemplate(
                          produce((draft) => {
                            draft!.description = v.target.value;
                          })
                        );
                      }}
                      validationProps={{
                        formId: formId,
                        formSectionId: CreateTemplateGlobalSectionId,
                        fieldId: "Name",
                        initialState: false,
                        required: true,
                      }}
                      disabled={false}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <FormAutocomplete
                      label={t("field:label.productType")}
                      validationProps={{
                        formId: formId,
                        formSectionId: CreateTemplateGlobalSectionId,
                        fieldId: "product-type",
                        required: true,
                      }}
                      onChange={(value) => {
                        setTemplate(
                          produce((draft) => {
                            draft!.productType = value !== null ? value.type : null;
                          })
                        );
                      }}
                      placeholder={t("field:placeholder.selectProductType")}
                      options={products}
                      value={products.find((x) => x.type === template.productType) || null}
                      getOptionLabel={(option: any) => option.name}
                      filterOptions={(x, s) => x.filter((a) => a.name?.includesAllWords(s.inputValue))}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("common:group")}</InputLabel>
                    <OutlinedInput
                      sx={{ width: "100%", mb: 2 }}
                      value={template.group}
                      placeholder={t("common:group")}
                      onChange={(v) => {
                        setTemplate(
                          produce((draft) => {
                            draft!.group = v.target.value;
                          })
                        );
                      }}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.isInternalErpManaged")}</InputLabel>

                    {context === "Update" ? (
                      <Typography color={template?.isInternal ? "success.main" : "error"} mt={2}>
                        {template?.isInternal ? t("common:yesUpperCaseAll") : t("common:noUpperCaseAll")}
                      </Typography>
                    ) : (
                      <Switch
                        size="medium"
                        checked={template.isInternal || false}
                        onChange={(e) =>
                          setTemplate(
                            produce((draft) => {
                              draft!.isInternal = e.target.checked;
                            })
                          )
                        }
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.filterAccessForUserGroups")}</InputLabel>
                    <Autocomplete
                      options={groups}
                      getOptionLabel={(option: any) => option.group.name}
                      isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                      multiple
                      value={groups.filter((x) => template.accessGroupIds?.includes(x.id))}
                      onChange={(e, v) =>
                        setTemplate(
                          produce((draft) => {
                            draft!.accessGroupIds = v.map((x) => x.id);
                          })
                        )
                      }
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          required
                          sx={{ width: "100%" }}
                          placeholder={t("field:placeholder.selectGroup")}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.filterLocationAccess")}</InputLabel>
                    <Autocomplete
                      options={Object.keys(ManualTasktemplateAccessibility)}
                      multiple
                      value={Object.keys(ManualTasktemplateAccessibility).filter((x) =>
                        template.accessibilities?.includes(x as ManualTasktemplateAccessibility)
                      )}
                      onChange={(e, v) =>
                        setTemplate(
                          produce((draft) => {
                            draft!.accessibilities = v as ManualTasktemplateAccessibility[];
                          })
                        )
                      }
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          required
                          sx={{ width: "100%" }}
                          placeholder={t("field:placeholder.selectLocationAccess")}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} mt={0.6}>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.enableUserTasksCreation")}</InputLabel>
                    <Switch
                      size="medium"
                      checked={template.createUserTasks || false}
                      onChange={(e) =>
                        setTemplate(
                          produce((draft) => {
                            draft!.createUserTasks = e.target.checked;
                          })
                        )
                      }
                    />
                  </Grid>
                  <Grid item md={6}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.templateIsTreatedExternally")}</InputLabel>
                    <Switch
                      size="medium"
                      checked={template.isTreatedExternally || false}
                      onChange={(e) =>
                        setTemplate(
                          produce((draft) => {
                            draft!.isTreatedExternally = e.target.checked;
                          })
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </FormSection>

              {/* ERP Connection Configuration */}
              {template.isInternal && UserHasAcl(AclTypes.aclSuperUser) && (
                <FormSection
                  title={t("form:section.title.erpConnectionConfiguration")}
                  sx={{ mb: 2 }}
                  status={isERPConfigureSectionValid ? "complete" : "warning"}
                  statusText={isERPConfigureSectionValid ? t("common:configured") : t("common:configurationRequired")}
                >
                  {/*  */}
                  <Grid container spacing={2} mb={1}>
                    <Grid item md={4}>
                      <FormInputText
                        label={t("field:label.accountManagerEmail")}
                        placeholder={t("common:email")}
                        value={template.erpHeaderInfos?.accountManagerEmail}
                        onChange={(v) => {
                          setTemplate(
                            produce((draft) => {
                              if (!draft?.erpHeaderInfos) draft!.erpHeaderInfos = new ERPWorkOrderHeaderInfos();
                              draft!.erpHeaderInfos.accountManagerEmail = v.target.value;
                            })
                          );
                        }}
                        validationProps={{
                          formId: formId,
                          formSectionId: CreateTemplateERPSectionId,
                          fieldId: "email",
                          initialState: false,
                          required: true,
                        }}
                      />
                    </Grid>
                    <Grid item md={4}>
                      <FormInputText
                        label={t("field:label.clientId")}
                        placeholder={t("field:placeholder.clientId")}
                        value={template.erpHeaderInfos?.clientId}
                        onChange={(v) => {
                          setTemplate(
                            produce((draft) => {
                              if (!draft?.erpHeaderInfos) draft!.erpHeaderInfos = new ERPWorkOrderHeaderInfos();
                              draft!.erpHeaderInfos.clientId = v.target.value;
                            })
                          );
                        }}
                        validationProps={{
                          formId: formId,
                          formSectionId: CreateTemplateERPSectionId,
                          fieldId: "id",
                          initialState: false,
                          required: true,
                        }}
                      />
                    </Grid>
                    <Grid item md={4}>
                      <FormInputText
                        label={t("field:label.projectName")}
                        placeholder={t("field:placeholder.projectName")}
                        value={template.erpHeaderInfos?.projectName}
                        onChange={(v) => {
                          setTemplate(
                            produce((draft) => {
                              if (!draft?.erpHeaderInfos) draft!.erpHeaderInfos = new ERPWorkOrderHeaderInfos();
                              draft!.erpHeaderInfos.projectName = v.target.value;
                            })
                          );
                        }}
                        validationProps={{
                          formId: formId,
                          formSectionId: CreateTemplateERPSectionId,
                          fieldId: "projectName",
                          initialState: false,
                          required: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Alert
                    icon={<InfoOutlinedIcon />}
                    color="warning"
                    sx={{ mb: 1.5, fontWeight: 1, fontSize: 13, height: 33, width: "auto" }}
                  >
                    {t("alert:warning.followingInfoAreRestricted")}
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item md={4}>
                      <InputLabel sx={{ mb: 1 }}>{t("field:label.erpWorkOrderCreationMode")}</InputLabel>
                      <Autocomplete
                        options={Object.keys(OrderERPCreationMode)}
                        value={template.erpTriggerInfos?.erpWorkOrderCreationMode}
                        onChange={(e, v) =>
                          setTemplate(
                            produce((draft) => {
                              if (!draft!.erpTriggerInfos) draft!.erpTriggerInfos = new ERPWorkOrderTriggerInfos();
                              draft!.erpTriggerInfos.erpWorkOrderCreationMode = v as OrderERPCreationMode;
                            })
                          )
                        }
                        // popupIcon={<ExpandMoreRoundedIcon />}
                        renderInput={(params) => (
                          <OutlinedInput
                            {...params.InputProps}
                            inputProps={params.inputProps}
                            required
                            sx={{ width: "100%" }}
                            placeholder={t("field:placeholder.selectCreationMode")}
                          />
                        )}
                      />{" "}
                    </Grid>
                    <Grid item md={4}>
                      <InputLabel sx={{ mb: 1 }}>{t("field:label.workflowStepNameTrigger")}</InputLabel>
                      <OutlinedInput
                        sx={{ width: "100%" }}
                        value={template.erpTriggerInfos?.erpWorkOrderWorkflowStepNameCreationTrigger}
                        placeholder={t("field:placeholder.workflowStepNameTrigger")}
                        onChange={(v) => {
                          setTemplate(
                            produce((draft) => {
                              if (!draft!.erpTriggerInfos) draft!.erpTriggerInfos = new ERPWorkOrderTriggerInfos();
                              draft!.erpTriggerInfos.erpWorkOrderWorkflowStepNameCreationTrigger = v.target.value;
                            })
                          );
                        }}
                      />
                    </Grid>
                    <Grid item md={4}>
                      <InputLabel sx={{ mb: 1 }}>{t("field:label.completeErpTask")}</InputLabel>
                      <Switch
                        size="medium"
                        checked={template.erpTriggerInfos?.erpWorkOrderCompleteTaskOnCompletedWorkflow ?? false}
                        onChange={(e) =>
                          setTemplate(
                            produce((draft) => {
                              if (!draft!.erpTriggerInfos) draft!.erpTriggerInfos = new ERPWorkOrderTriggerInfos();
                              draft!.erpTriggerInfos.erpWorkOrderCompleteTaskOnCompletedWorkflow = e.target.checked;
                            })
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                </FormSection>
              )}

              {/* Configure Jobs & Tasks as a Workflow model...*/}
              <FormSection
                title={t("form:section.title.configureJobsAndTasks")}
                sx={{ mb: 2 }}
                status={template.jobs && template.jobs?.length > 0 ? "complete" : "warning"}
                statusText={
                  template.jobs && template.jobs?.length > 0
                    ? t("common:configured")
                    : t("common:configurationRequired")
                }
              >
                {template.jobs &&
                  template.jobs.length > 0 &&
                  template.jobs.map((job, index) => (
                    <>
                      <Box mb={1}>
                        <MetadataBoxAccordion
                          title={t("page:metadataBoxAccordion.title.jobIdAndName", {
                            index: index + 1,
                            name: job.name || "",
                          })}
                          icon={<FormatListBulletedIcon />}
                          sx={{ mb: 0 }}
                          rightContent={
                            <>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Chip
                                  label={
                                    (job.tasks?.reduce((accumulator, task) => {
                                      const priceObject = task.prices?.find((x) => x.currency === "USD");
                                      return accumulator + (priceObject?.price || 0);
                                    }, 0) || 0) + "USD"
                                  }
                                  key={index}
                                  color="secondary"
                                  size="small"
                                />
                                <Chip
                                  label={
                                    (job.tasks?.reduce((accumulator, task) => {
                                      const priceObject = task.prices?.find((x) => x.currency === "EUR");
                                      return accumulator + (priceObject?.price || 0);
                                    }, 0) || 0) + "EUR"
                                  }
                                  key={index}
                                  color="secondary"
                                  size="small"
                                />
                                <Chip label={job.service?.name} color="primary" size="medium" />
                                <Stack direction="row" alignItems="center">
                                  {index !== 0 && (
                                    <IconButton
                                      onClick={() => {
                                        setTemplate(
                                          produce((draft) => {
                                            let index = draft!.jobs?.findIndex((x) => x.name === job.name);
                                            const itemToMove = draft!.jobs![index!];
                                            draft!.jobs![index!] = draft!.jobs![index! - 1];
                                            draft!.jobs![index! - 1] = itemToMove;
                                          })
                                        );
                                      }}
                                    >
                                      <ArrowUpwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                                    </IconButton>
                                  )}
                                  {template.jobs && template.jobs[index + 1] !== undefined && (
                                    <IconButton
                                      onClick={() => {
                                        setTemplate(
                                          produce((draft) => {
                                            let index = draft!.jobs?.findIndex((x) => x.name === job.name);
                                            const itemToMove = draft!.jobs![index!];
                                            draft!.jobs![index!] = draft!.jobs![index! + 1];
                                            draft!.jobs![index! + 1] = itemToMove;
                                          })
                                        );
                                      }}
                                    >
                                      <ArrowDownwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            </>
                          }
                        >
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "backgroundAlpha.main",
                              borderRadius: "5px",
                              padding: "15px",
                            }}
                          >
                            {/* Job table header */}
                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid item md={3}>
                                <Typography variant="body2" color="text.tier">
                                  {t("common:name")}
                                </Typography>
                              </Grid>
                              <Grid item md={3}>
                                <Typography variant="body2" color="text.tier">
                                  {t("common:description")}
                                </Typography>
                              </Grid>
                              <Grid item md={1.5}>
                                <Typography variant="body2" color="text.tier">
                                  {t("common:erpTaskCode")}
                                </Typography>
                              </Grid>
                              <Grid item md={2}>
                                <Typography variant="body2" color="text.tier">
                                  {t("common:prices")}
                                </Typography>
                              </Grid>
                              <Grid item md={1}>
                                <Typography variant="body2" color="text.tier">
                                  {t("table:headerName.canBeDisabled")}
                                </Typography>
                              </Grid>
                              <Grid item md={1.5}>
                                <Typography variant="body2" color="text.tier">
                                  {t("common:actions")}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Divider sx={{ mb: 2 }} />

                            {/* table rows */}
                            {job.tasks &&
                              job.tasks?.map((task, index) => (
                                <Grid container spacing={2} sx={{ mb: 1 }} key={index}>
                                  <Grid item md={3}>
                                    <Typography>{task.name ? task.name : "-"}</Typography>
                                  </Grid>
                                  <Grid item md={3}>
                                    <TableCellDescription title="" description={task.description || "-"} />
                                  </Grid>
                                  <Grid item md={1.5}>
                                    <Typography>{task.erpTaskCode ? task.erpTaskCode : "-"}</Typography>
                                  </Grid>
                                  <Grid item md={2}>
                                    {task.prices ? (
                                      <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                                        {task.prices?.map((x, index) => (
                                          <Chip
                                            label={x.price + " " + x.currency}
                                            key={index}
                                            color="secondary"
                                            size="small"
                                          />
                                        ))}
                                      </Stack>
                                    ) : (
                                      ""
                                    )}
                                  </Grid>
                                  <Grid item md={1}>
                                    <Switch
                                      size="medium"
                                      checked={task.canBeDisabled || false}
                                      onChange={(e) => {
                                        setTemplate(
                                          produce((draft) => {
                                            draft?.jobs?.forEach((job) => {
                                              if (job.tasks)
                                                job.tasks.forEach((myTask) => {
                                                  if (myTask.id === task.id) myTask.canBeDisabled = e.target.checked;
                                                });
                                            });
                                          })
                                        );
                                      }}
                                    />
                                  </Grid>
                                  <Grid item md={1}>
                                    <Link
                                      href="#"
                                      underline="hover"
                                      variant="subtitle1"
                                      color="error"
                                      fontWeight={500}
                                      fontSize={14}
                                      onClick={() =>
                                        setTemplate(
                                          produce((draft) => {
                                            draft?.jobs?.forEach((job) => {
                                              if (job.tasks)
                                                job.tasks.forEach((myTask) => {
                                                  if (myTask.id === task.id)
                                                    job.tasks = job.tasks?.filter((x) => x.id !== task.id) || [];
                                                });
                                            });
                                          })
                                        )
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                      {t("common:remove")}
                                    </Link>
                                  </Grid>

                                  <Grid item md={0.25}>
                                    {job.tasks && job.tasks[index + 1] !== undefined && (
                                      <IconButton
                                        onClick={() => {
                                          let done = false;
                                          setTemplate(
                                            produce((draft) => {
                                              draft?.jobs?.forEach((job) => {
                                                if (job.tasks)
                                                  job.tasks.forEach((myTask) => {
                                                    if (myTask.id === task.id) {
                                                      let index = job.tasks?.indexOf(myTask);
                                                      if (
                                                        job.tasks!.length > 1 &&
                                                        index! < job.tasks!.length - 1 &&
                                                        !done
                                                      ) {
                                                        const itemToMove = job.tasks![index!];
                                                        job.tasks![index!] = job.tasks![index! + 1];
                                                        job.tasks![index! + 1] = itemToMove;
                                                        done = true;
                                                      }
                                                    }
                                                  });
                                              });
                                            })
                                          );
                                        }}
                                      >
                                        <ArrowDownwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                                      </IconButton>
                                    )}
                                  </Grid>
                                  <Grid item md={0.25}>
                                    {index !== 0 && (
                                      <IconButton
                                        onClick={() =>
                                          setTemplate(
                                            produce((draft) => {
                                              draft?.jobs?.forEach((job) => {
                                                if (job.tasks)
                                                  job.tasks.forEach((myTask) => {
                                                    if (myTask.id === task.id) {
                                                      let index = job.tasks?.indexOf(myTask);
                                                      if (job.tasks!.length > 0 && index! > 0) {
                                                        const itemToMove = job.tasks![index!];
                                                        job.tasks![index!] = job.tasks![index! - 1];
                                                        job.tasks![index! - 1] = itemToMove;
                                                      }
                                                    }
                                                  });
                                              });
                                            })
                                          )
                                        }
                                      >
                                        <ArrowUpwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                                      </IconButton>
                                    )}
                                  </Grid>
                                </Grid>
                              ))}

                            {job.tasks === null && (
                              <Typography variant="body2" color="text.tier">
                                {t("page:body.noTasksFound")}
                              </Typography>
                            )}
                          </Box>

                          {/* Job actions */}
                          <Grid container spacing={2} mt={1}>
                            <Grid item md={6}>
                              {/* Add task to job */}
                              <Grid container spacing={2}>
                                <Grid item md={6}>
                                  <Autocomplete
                                    options={tasks.find((s) => s.service?.id === job.service?.id)?.tasks || []}
                                    getOptionLabel={(option: any) => option.name}
                                    value={newTasksJob.find((x) => x.name! === job.name)?.taskType}
                                    onChange={(e, v) => {
                                      setNewTasksJob(
                                        produce((draft) => {
                                          let item = draft.find((a) => a.name === job.name);
                                          if (item) item!.taskType = v;
                                        })
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <OutlinedInput
                                        {...params.InputProps}
                                        inputProps={params.inputProps}
                                        required
                                        sx={{ width: "100%" }}
                                        placeholder={t("field:placeholder.selectATask")}
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item md={4}>
                                  <Button
                                    startIcon={<AddIcon sx={{ mr: -0.5 }} />}
                                    variant="outlined"
                                    color="primary"
                                    disabled={newTasksJob.find((x) => x.name! === job.name)?.taskType === null}
                                    onClick={() => {
                                      let taskToAdd = newTasksJob.find((x) => x.name === job.name)?.taskType;
                                      setTemplate(
                                        produce((draft) => {
                                          let item = draft!.jobs?.find((a) => a.name === job.name);
                                          if (item && taskToAdd) {
                                            if (item!.tasks) item!.tasks?.push(taskToAdd);
                                            else item!.tasks = [taskToAdd];
                                          }
                                        })
                                      );
                                      setNewTasksJob(
                                        produce((draft) => {
                                          let item = draft.find((x) => x.name === job.name);
                                          if (item) {
                                            item.taskType = null;
                                          }
                                        })
                                      );
                                    }}
                                  >
                                    {t("button:addTaskToJob")}
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Rename + Remove job */}
                            <Grid item md={6}>
                              <Stack direction="row">
                                <Grid container spacing={1} justifyContent="flex-end">
                                  <Grid item md={6}>
                                    <OutlinedInput
                                      sx={{ mb: 2, flexGrow: 1, width: "100%" }}
                                      placeholder={t("field:placeholder.jobName")}
                                      value={newTasksJob.find((x) => x.name === job.name)!.newName || ""}
                                      onChange={(v) => {
                                        setNewTasksJob(
                                          produce((draft) => {
                                            let item = draft.find((x) => x.name === job.name);
                                            if (item) item.newName = v.target.value;
                                          })
                                        );
                                      }}
                                    />
                                  </Grid>
                                  <Grid item md={3}>
                                    <Button
                                      startIcon={<EditIcon sx={{ mr: -0.5 }} />}
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => {
                                        let newName = newTasksJob.find((x) => x.name === job.name)?.newName;
                                        if (newName)
                                          setTemplate(
                                            produce((draft) => {
                                              let item = draft!.jobs?.find((x) => x.name === job.name);
                                              if (item && newName !== undefined) item.name = newName;
                                            })
                                          );
                                        setNewTasksJob(
                                          produce((draft) => {
                                            let item = draft.find((x) => x.newName === newName);
                                            if (item && newName !== undefined) {
                                              item.name = newName;
                                              item.newName = "";
                                            }
                                          })
                                        );
                                      }}
                                      disabled={newTasksJob.find((x) => x.name! === job.name)?.newName === ""}
                                    >
                                      {t("button:renameJob")}
                                    </Button>
                                  </Grid>
                                  <Grid item md={3}>
                                    <Button
                                      startIcon={<DeleteIcon sx={{ mr: -0.5 }} />}
                                      variant="outlined"
                                      color="error"
                                      onClick={() => {
                                        setTemplate(
                                          produce((draft) => {
                                            if (draft!.jobs!.length === 1) draft!.jobs = [];
                                            else {
                                              let index = draft?.jobs?.findIndex((x) => x.name === job.name);

                                              if (index! > -1) {
                                                draft!.jobs?.splice(index!, 1);
                                              }
                                            }
                                          })
                                        );
                                      }}
                                    >
                                      {t("button:removeJob")}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MetadataBoxAccordion>
                      </Box>
                    </>
                  ))}
                {template!.jobs === null ||
                  (template.jobs.length === 0 && (
                    <Typography variant="body2" color="text.tier">
                      {t("page:body.noJobsFound")}
                    </Typography>
                  ))}
              </FormSection>

              {/* Add a Job into Workflow model... */}
              <FormSection title={t("form:section.title.addJobIntoWorkflowModel")} status="todo" sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item md={4}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.selectDestinationService")}</InputLabel>
                    <Autocomplete
                      options={services}
                      getOptionLabel={(option: any) => option.name}
                      isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                      filterOptions={(x, s) => x.filter((a) => a.name?.includesAllWords(s.inputValue))}
                      value={newJob.service}
                      onChange={(e, v) => {
                        setNewJob(
                          produce((draft) => {
                            draft!.service = v;
                          })
                        );
                      }}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          required
                          sx={{ width: "100%" }}
                          placeholder={t("field:placeholder.selectDestinationService")}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3.5}>
                    <InputLabel sx={{ mb: 1 }}>{t("common:name")}</InputLabel>
                    <OutlinedInput
                      sx={{ width: "100%", mb: 2 }}
                      value={newJob.name || ""}
                      placeholder={t("common:name")}
                      onChange={(v) => {
                        setNewJob(
                          produce((draft) => {
                            draft!.name = v.target.value;
                          })
                        );
                      }}
                    />
                  </Grid>
                  <Grid item md={3.5}>
                    <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
                    <OutlinedInput
                      placeholder={t("common:description")}
                      sx={{ width: "100%", mb: 2 }}
                      value={newJob.description || ""}
                      onChange={(v) => {
                        setNewJob(
                          produce((draft) => {
                            draft!.description = v.target.value;
                          })
                        );
                      }}
                    />
                  </Grid>
                  <Grid item md={1}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.addJob")}</InputLabel>
                    <IconButton
                      onClick={() => {
                        setNewTasksJob(
                          produce((draft) => {
                            draft!.push({ name: newJob.name || "", newName: "", taskType: null });
                          })
                        );
                        setTemplate(
                          produce((draft) => {
                            draft!.jobs?.push(newJob);
                          })
                        );
                        setNewJob(
                          new DbOrderManualTaskJobInfos({
                            $type: "VDM.AirLab.Sdk.Core.Database.Types.DbOrderManualTaskJobInfos, VDM.AirLab.Sdk.Core",
                          })
                        );
                      }}
                      disabled={newJob?.service === null || newJob.name === null || newJob.name === ""}
                    >
                      <AddIcon sx={{ width: "18px", height: "18px" }} />
                    </IconButton>
                  </Grid>
                </Grid>
              </FormSection>

              {/* Metadata Fields Form */}
              <FormSection title={t("form:section.title.metadataFieldsForm")} status="todo" sx={{ mb: 2 }}>
                {template.formFields && (
                  <Box
                    sx={{
                      mb: 2,
                      border: "1px solid",
                      borderColor: "backgroundAlpha.main",
                      borderRadius: "5px",
                      padding: "15px",
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item md={1}>
                        <Typography variant="body2" color="text.tier">
                          {t("common:type")}
                        </Typography>
                      </Grid>
                      <Grid item md={2.5}>
                        <Typography variant="body2" color="text.tier">
                          {t("common:name")}
                        </Typography>
                      </Grid>
                      <Grid item md={2}>
                        <Typography variant="body2" color="text.tier">
                          {t("common:labelUpperCase")}
                        </Typography>
                      </Grid>
                      <Grid item md={1}>
                        <Typography variant="body2" color="text.tier">
                          {t("table:headerName.isMandatory")}
                        </Typography>
                      </Grid>
                      <Grid item md={3}>
                        <Typography variant="body2" color="text.tier">
                          {t("common:possibleValues")}
                        </Typography>
                      </Grid>
                      <Grid item md={1}>
                        <Typography variant="body2" color="text.tier">
                          {t("field:label.cardinality")}
                        </Typography>
                      </Grid>
                      <Grid item md={1.5}>
                        <Typography variant="body2" color="text.tier">
                          {t("common:actions")}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {template.formFields?.map((formField, index) => (
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item md={1}>
                          <Typography>{formField.baseType || "-"}</Typography>
                        </Grid>
                        <Grid item md={2.5}>
                          <Typography>{formField.name || "-"}</Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography>{formField.label || "-"}</Typography>
                        </Grid>
                        <Grid item md={1}>
                          <TableCellBool value={formField.isMandatory} />
                        </Grid>
                        <Grid item md={3}>
                          {formField.predefinedValues ? (
                            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                              {formField.predefinedValues.map((x, index) => (
                                <Chip label={x} key={index} color="secondary" size="small" />
                              ))}
                            </Stack>
                          ) : (
                            "-"
                          )}
                        </Grid>
                        <Grid item md={1}>
                          <Chip label={formField.valuesCardinality} color="primary" size="small" />
                        </Grid>
                        <Grid item md={1}>
                          <Link
                            href="#"
                            underline="hover"
                            variant="subtitle1"
                            color="error"
                            fontWeight={500}
                            fontSize={14}
                            onClick={() =>
                              setTemplate(
                                produce((draft) => {
                                  let index = draft!.formFields?.findIndex(
                                    (x) =>
                                      x.baseType === formField.baseType &&
                                      x.name === formField.name &&
                                      x.valuesCardinality === formField.valuesCardinality &&
                                      x.label === formField.label &&
                                      x.isMandatory === formField.isMandatory
                                  );
                                  if (index !== -1) draft!.formFields?.splice(index!, 1);
                                })
                              )
                            }
                          >
                            <DeleteIcon fontSize="small" />
                            {t("common:remove")}
                          </Link>
                        </Grid>

                        <Grid item md={0.25}>
                          {template.formFields && template.formFields[index + 1] !== undefined && (
                            <IconButton
                              onClick={() => {
                                setTemplate(
                                  produce((draft) => {
                                    let index = draft!.formFields?.findIndex(
                                      (x) =>
                                        x.baseType === formField.baseType &&
                                        x.name === formField.name &&
                                        x.valuesCardinality === formField.valuesCardinality &&
                                        x.label === formField.label &&
                                        x.isMandatory === formField.isMandatory
                                    );
                                    const itemToMove = draft!.formFields![index!];
                                    draft!.formFields![index!] = draft!.formFields![index! + 1];
                                    draft!.formFields![index! + 1] = itemToMove;
                                  })
                                );
                              }}
                            >
                              <ArrowDownwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                            </IconButton>
                          )}
                        </Grid>
                        <Grid item md={0.25}>
                          {index !== 0 && (
                            <IconButton
                              onClick={() => {
                                setTemplate(
                                  produce((draft) => {
                                    let index = draft!.formFields?.findIndex(
                                      (x) =>
                                        x.baseType === formField.baseType &&
                                        x.name === formField.name &&
                                        x.valuesCardinality === formField.valuesCardinality &&
                                        x.label === formField.label &&
                                        x.isMandatory === formField.isMandatory
                                    );
                                    const itemToMove = draft!.formFields![index!];
                                    draft!.formFields![index!] = draft!.formFields![index! - 1];
                                    draft!.formFields![index! - 1] = itemToMove;
                                  })
                                );
                              }}
                            >
                              <ArrowUpwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    ))}

                    {template.formFields.length === 0 && (
                      <Typography variant="body2" color="text.tier">
                        {t("page:body.noFieldsFound")}
                      </Typography>
                    )}
                  </Box>
                )}

                <Grid container spacing={2} mt={2}>
                  <Grid item md={2}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.fieldType")}</InputLabel>
                    <Autocomplete
                      options={["String", "Long", "Boolean", "DateTime"]}
                      value={newMeta.baseType}
                      onChange={(e, v) => {
                        setNewMeta(
                          produce((draft) => {
                            draft!.baseType = v as MetadataFieldBaseTypeEnum;
                          })
                        );
                      }}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          required
                          sx={{ width: "100%" }}
                          placeholder={t("field:placeholder.selectFieldType")}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={2}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.nameSystemKey")}</InputLabel>
                    <OutlinedInput
                      sx={{ width: "100%", mb: 2 }}
                      value={newMeta.name || ""}
                      placeholder="Name"
                      onChange={(v) => {
                        setNewMeta(
                          produce((draft) => {
                            draft!.name = v.target.value;
                          })
                        );
                      }}
                    />
                  </Grid>
                  <Grid item md={2}>
                    <InputLabel sx={{ mb: 1 }}>{t("common:labelUpperCase")}</InputLabel>
                    <OutlinedInput
                      sx={{ width: "100%", mb: 2 }}
                      placeholder={t("common:labelUpperCase")}
                      value={newMeta.label || ""}
                      onChange={(v) => {
                        setNewMeta(
                          produce((draft) => {
                            draft!.label = v.target.value;
                          })
                        );
                      }}
                    />
                  </Grid>
                  <Grid item md={1}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.isMandatory")}</InputLabel>
                    <Switch
                      size="medium"
                      checked={newMeta.isMandatory || false}
                      onChange={(e) =>
                        setNewMeta(
                          produce((draft) => {
                            draft!.isMandatory = e.target.checked;
                          })
                        )
                      }
                    />
                  </Grid>
                  <Grid item md={2}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.possibleValues")}</InputLabel>
                    <Autocomplete
                      value={newMeta.predefinedValues || []}
                      multiple
                      freeSolo
                      onChange={(e, value) =>
                        setNewMeta(
                          produce((draft) => {
                            draft!.predefinedValues = value;
                          })
                        )
                      }
                      options={[]}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("common:possibleValues")} />
                      )}
                    />
                  </Grid>
                  <Grid item md={2}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.cardinality")}</InputLabel>
                    <Autocomplete
                      options={["One", "N"]}
                      value={newMeta.valuesCardinality}
                      onChange={(e, v) => {
                        setNewMeta(
                          produce((draft) => {
                            draft!.valuesCardinality = v as MetadataFieldValueCardinality;
                          })
                        );
                      }}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          required
                          sx={{ width: "100%" }}
                          placeholder={t("field:placeholder.selectCardinality")}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={1}>
                    <InputLabel sx={{ mb: 1 }}>{t("field:label.addField")}</InputLabel>
                    <IconButton
                      disabled={
                        newMeta.baseType === null ||
                        newMeta.name === null ||
                        newMeta.name === "" ||
                        newMeta.label === null ||
                        newMeta.label === "" ||
                        newMeta.valuesCardinality === null
                      }
                      onClick={() => {
                        setTemplate(
                          produce((draft) => {
                            draft!.formFields?.push(newMeta);
                          })
                        );
                        setNewMeta(new DbOrderManualTaskTemplateFormFieldInfos());
                      }}
                    >
                      <AddIcon sx={{ width: "18px", height: "18px" }} />
                    </IconButton>
                  </Grid>
                </Grid>

                <Alert
                  icon={<InfoOutlinedIcon />}
                  color="info"
                  sx={{ mb: 1.5, fontWeight: 1, fontSize: 13, height: 33, width: "auto" }}
                >
                  {t("alert:info.noteWhenNotSetPossibleValues")}
                </Alert>
              </FormSection>

              {/* Total */}
              <FormSection title={t("common:total")} status="todo" sx={{ mb: 2 }}>
                <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                  <Chip label={GetTotalPrice("EUR") + "EUR"} color="secondary" size="medium" />
                  <Chip label={GetTotalPrice("USD") + "USD"} color="secondary" size="medium" />
                </Stack>
              </FormSection>

              {/* Buttons */}
              <Stack mt={3} direction="row" justifyContent="flex-end" spacing={2}>
                {context === "Update" && (
                  <>
                    <Button size="medium" variant="outlined" color="error" onClick={handleDelete}>
                      {t("common:delete")}
                    </Button>
                    <Button
                      size="medium"
                      variant="outlined"
                      color="success"
                      onClick={handleUpdate}
                      disabled={template.jobs?.length === 0 || !formValid}
                    >
                      {t("common:update")}
                    </Button>
                  </>
                )}
                {context === "Create" && (
                  <Button
                    size="medium"
                    variant="outlined"
                    color="success"
                    onClick={handleCreate}
                    disabled={template.jobs?.length === 0 || !formValid}
                  >
                    {t("common:create")}
                  </Button>
                )}
              </Stack>
            </Paper>
          </Box>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default AdminSettingsManualTasksTemplateConfig;
